import { SEGMENT_CUSTOM_FEATURE } from '../common/selection.menu/constants';
import {
  BooleanInstruction,
  ContentInstruction,
  ContentInstructions,
  FromSettingsInstruction,
  NumberInstruction,
  ObjectInstruction,
  Params,
  StringInstruction,
  StringListInstruction,
} from '../types';

export class Instructions {
  constructor(
    private readonly instructions: ContentInstructions,
    private readonly settings: Params
  ) {}

  hasInstruction(name: string): boolean {
    return this.instructions[name] !== undefined;
  }

  getInstruction(name: string): ContentInstruction | undefined {
    return this.instructions[name];
  }

  isFromSettings(name: string): boolean {
    return this.getInstruction(name)?.type === 'FromSettingsInstruction';
  }

  getSettingsName(name: string): string {
    if (!this.isFromSettings(name)) {
      throw new Error(`instruction ${name} is not from settings type`);
    }
    const instruction = this.getInstruction(name);
    if (!instruction) {
      throw new Error(`instruction ${name} not found`);
    }
    return (instruction as FromSettingsInstruction).instruction;
  }

  getString(name: string, defaultValue?: string): string {
    const instruction = this.getInstruction(name);
    if (!instruction) {
      return defaultValue;
    }

    if (instruction.type === 'FromSettingsInstruction') {
      let value = this.settings[(instruction as FromSettingsInstruction).instruction]?.[0];
      if (value?.includes('|')) {
        value = value.substring(0, value.indexOf('|'));
      }
      return value;
    } else if (instruction.type === 'StringInstruction') {
      return (instruction as StringInstruction).instruction;
    }

    throw new Error(`instruction ${name} is not type string or from settings`);
  }

  getStringList(name: string, defaultValue?: string[]): string[] {
    const instruction = this.getInstruction(name);
    if (!instruction) {
      return defaultValue;
    }

    if (instruction.type === 'FromSettingsInstruction') {
      return this.settings[(instruction as FromSettingsInstruction).instruction];
    } else if (instruction.type === 'StringListInstruction') {
      return (instruction as StringListInstruction).instruction;
    }

    throw new Error(`instruction ${name} is not type string list or from settings`);
  }

  getObject(name: string, defaultValue?: unknown): unknown {
    const instruction = this.getInstruction(name);
    if (!instruction) {
      return defaultValue;
    }

    if (instruction.type === 'ObjectInstruction') {
      return (instruction as ObjectInstruction).instruction;
    }

    throw new Error(`instruction ${name} is not type object`);
  }

  getNumber(name: string, defaultValue?: number): number {
    const instruction = this.getInstruction(name);
    if (instruction === undefined) {
      return defaultValue;
    }

    if (instruction.type === 'NumberInstruction') {
      return (instruction as NumberInstruction).instruction;
    }

    throw new Error(`instruction ${name} is not type number`);
  }

  getBoolean(name: string, defaultValue?: boolean): boolean {
    const instruction = this.getInstruction(name);
    if (!instruction) {
      return defaultValue;
    }

    if (instruction.type === 'BooleanInstruction') {
      return (instruction as BooleanInstruction).instruction;
    }

    throw new Error(`instruction ${name} is not type boolean`);
  }

  getResolvedFieldName(name: string, menuType?: 'primary' | 'secondary', defaultValue?: string): string {
    const originalValue = this.getString(name, defaultValue);

    if (!originalValue?.includes(SEGMENT_CUSTOM_FEATURE)) {
      return originalValue;
    }

    const baseFieldName = originalValue.includes('|')
      ? originalValue.substring(0, originalValue.indexOf('|'))
      : originalValue;

    if (baseFieldName !== SEGMENT_CUSTOM_FEATURE) {
      return baseFieldName;
    }

    const customFeatureTiers = this.settings.customFeatureTiers || [];
    const bothHaveCustomFeatures =
      customFeatureTiers.some(tier => tier.startsWith('grouping:')) &&
      customFeatureTiers.some(tier => tier.startsWith('sgSegment:'));

    const bothMenusAreCustom =
      (this.settings.grouping?.[0] || '').includes(SEGMENT_CUSTOM_FEATURE) &&
      (this.settings.sgSegment?.[0] || '').includes(SEGMENT_CUSTOM_FEATURE);

    if (!bothHaveCustomFeatures || !bothMenusAreCustom) {
      return baseFieldName;
    }

    if (menuType === 'primary') return 'segment_custom_feature_primary';
    if (menuType === 'secondary') return 'segment_custom_feature_secondary';

    if (this.isFromSettings(name)) {
      const settingsKey = this.getSettingsName(name);
      if (settingsKey === 'grouping') return 'segment_custom_feature_primary';
      if (settingsKey === 'sgSegment') return 'segment_custom_feature_secondary';
    }

    return baseFieldName;
  }
}
